import { useMemo, useState } from 'react';
import { ArrowLeft, AlertTriangle, Camera, Loader2, ShieldAlert, Skull, CheckCircle, AlertCircle, MapPin, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import ImagePicker from '../components/ImagePicker';
import AiCaution from '../components/AiCaution';
import { dataURLToFile, fileToCompressedDataURL } from '../lib/image';
import SpeciesIcon from '../components/species/SpeciesIcon';
import LogFindModal from '../components/LogFindModal';

const LEVEL_STYLES = {
    safe: { bg: 'bg-brand-green/15', border: 'border-brand-green/40', text: 'text-brand-green', icon: CheckCircle },
    caution: { bg: 'bg-yellow-100', border: 'border-yellow-400', text: 'text-yellow-700', icon: AlertCircle },
    toxic: { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-700', icon: ShieldAlert },
    deadly: { bg: 'bg-red-100', border: 'border-red-500', text: 'text-red-700', icon: Skull },
};

export default function MushroomIdentify() {
    const { locale } = useI18n();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [previews, setPreviews] = useState([]);
    const [b64s, setB64s] = useState([]);
    const [extraNotes, setExtraNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [logOpen, setLogOpen] = useState(false);
    const photos = useMemo(() => previews.map((preview) => dataURLToFile(preview, 'mushroom.jpg')), [previews]);
    const isPremium = !!user?.premium || !!user?.ai_payg_available; // AI access, not a subscription grant
    const MAX = 3;

    const handlePick = async (file) => {
        if (!file) return;
        if (b64s.length >= 1 && !isPremium) {
            toast.error('Multi-photo ID is a Premium feature');
            navigate('/premium');
            return;
        }
        if (b64s.length >= MAX) { toast.error(`Up to ${MAX} photos`); return; }
        try {
            const dataUrl = await fileToCompressedDataURL(file);
            const base64 = dataUrl.includes(',') ? dataUrl.split(',', 2)[1] : dataUrl;
            setPreviews((p) => [...p, dataUrl]);
            setB64s((b) => [...b, base64]);
            setResult(null);
        } catch {
            toast.error('Could not load image');
        }
    };

    const removeAt = (i) => {
        setPreviews((p) => p.filter((_, idx) => idx !== i));
        setB64s((b) => b.filter((_, idx) => idx !== i));
        setResult(null);
    };

    const submit = async () => {
        if (!b64s.length) return toast.error('Pick a mushroom photo first');
        if (!isPremium) {
            navigate('/premium');
            return;
        }
        setBusy(true);
        try {
            const r = await api.post('/ai/identify-mushroom', { images_base64: b64s, locale, extra_notes: extraNotes || undefined });
            setResult(r.data);
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (err?.response?.status === 402) {
                toast.error(locale === 'da' ? 'Se AI-pris og saldo under Premium.' : 'View AI pricing and balance under Premium.'); navigate('/premium');
            } else if (err?.code === 'ERR_CANCELED') {
                // Declining the cost dialog is not an analysis failure.
            } else if (err?.response?.status !== 401 && err?.response?.status !== 403) {
                toast.error(typeof d === 'string' ? d : 'Could not analyze');
            }
        } finally { setBusy(false); }
    };

    const level = result?.edibility_level || 'caution';
    const style = LEVEL_STYLES[level] || LEVEL_STYLES.caution;
    const LevelIcon = style.icon;

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="mushroom-id-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">AI mushroom ID</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-2">Identify a mushroom</h1>
            <p className="text-sm text-brand-ink3 mb-6">Snap a photo — the AI returns species, edibility level and dangerous look-alikes. <strong className="text-brand-terracotta">Always cross-check with a local expert before eating.</strong></p>

            <AiCaution className="mb-6" />

            {!isPremium && (
                <div className="mb-6 flex items-start gap-2 text-xs text-brand-terracotta bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-2xl p-3" data-testid="premium-required">
                    <AlertTriangle size={14} strokeWidth={1.8} className="flex-shrink-0 mt-0.5" />
                    <span>Mushroom ID is a Premium feature (safety-critical). <button onClick={() => navigate('/premium')} className="underline font-medium" data-testid="upsell-btn">Unlock Premium</button> to use it.</span>
                </div>
            )}

            <div className="bg-brand-card border border-brand-border rounded-3xl p-5 mb-4">
                <ImagePicker onSelect={handlePick} testId="picker-mushroom">
                    {(open) => (
                        <button
                            type="button"
                            onClick={open}
                            data-testid="btn-pick-mushroom"
                            className="w-full bg-brand-bg hover:bg-brand-bg2 border border-brand-border rounded-2xl px-4 py-8 text-center text-sm text-brand-ink3 inline-flex items-center justify-center gap-2"
                        >
                            <Camera size={16} /> {previews.length ? `Add another angle (${previews.length}/${MAX})` : 'Pick mushroom photo'}
                            {isPremium && <span className="text-[10px] text-brand-green">· up to {MAX}</span>}
                        </button>
                    )}
                </ImagePicker>
                {previews.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-2" data-testid="mushroom-previews">
                        {previews.map((src, i) => (
                            <div key={src} className="relative">
                                <img src={src} alt="" className="w-full aspect-square object-cover rounded-2xl border border-brand-border" />
                                <button type="button" onClick={() => removeAt(i)} className="absolute top-1 right-1 bg-brand-ink/70 text-white p-1 rounded-full hover:bg-brand-ink">
                                    <X size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                {!isPremium && previews.length >= 1 && (
                    <p className="text-[11px] text-brand-ochre mt-2">📸 Add up to 3 angles for a more accurate ID — <button onClick={() => navigate('/premium')} className="underline font-medium">unlock Premium</button>.</p>
                )}
                <label className="block mt-4">
                    <span className="text-xs uppercase tracking-widest font-semibold text-brand-ink3">Extra notes (optional)</span>
                    <textarea
                        data-testid="input-mushroom-notes"
                        value={extraNotes}
                        onChange={(e) => setExtraNotes(e.target.value)}
                        rows="2"
                        placeholder="Habitat, gill shape, smell, spore-print colour…"
                        className="mt-1 w-full bg-brand-bg border border-brand-border rounded-2xl px-4 py-3 resize-none text-sm"
                    />
                </label>
                <button
                    data-testid="btn-mushroom-identify"
                    onClick={submit}
                    disabled={busy || !b64s.length}
                    className="mt-4 w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                    {busy ? 'Analyzing…' : 'Identify mushroom'}
                </button>
            </div>

            {result && (
                <div className="space-y-4" data-testid="mushroom-result">
                    <div className="bg-brand-card border border-brand-border rounded-3xl p-5">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <SpeciesIcon wiki={result.species} emoji={result.emoji || '🍄'} name={result.name} size={64} />
                            <div className="flex-1 min-w-0">
                                <h2 className="font-serif text-2xl text-brand-ink">
                                    {result.name || 'Unknown'}
                                </h2>
                                {result.species && <p className="italic text-sm text-brand-ink3">{result.species}</p>}
                            </div>
                            {typeof result.confidence === 'number' && (
                                <span className="text-xs font-semibold text-brand-ink3" data-testid="confidence">{result.confidence}%</span>
                            )}
                        </div>
                        {result.edibility && (
                            <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${style.bg} ${style.border} ${style.text}`} data-testid="edibility-badge">
                                <LevelIcon size={12} /> {result.edibility}
                            </div>
                        )}
                        {(result.name || result.species) && <button type="button" data-testid="mushroom-log-find" onClick={() => setLogOpen(true)} className="mt-4 w-full inline-flex justify-center items-center gap-2 bg-brand-green text-white py-3 rounded-full text-sm"><MapPin size={15} />{locale === 'da' ? 'Gem privat i Vilde fund' : 'Save privately in Wild Finds'}</button>}
                    </div>

                    {/* MANDATORY WARNING */}
                    <div className="bg-red-50 border-2 border-red-300 rounded-3xl p-4 flex items-start gap-3" data-testid="safety-warning">
                        <Skull size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-900 leading-relaxed font-medium">
                            {result.warning || 'Never eat a mushroom based on a photo alone. Consult a local mycology expert.'}
                        </p>
                    </div>

                    {result.look_alikes && result.look_alikes.length > 0 && (
                        <div className="bg-brand-card border border-brand-border rounded-3xl p-5">
                            <h3 className="font-semibold text-brand-ink mb-2 inline-flex items-center gap-2"><AlertTriangle size={14} className="text-brand-terracotta" /> Dangerous look-alikes</h3>
                            <ul className="text-sm text-brand-ink list-disc list-inside space-y-0.5">
                                {result.look_alikes.map((la) => <li key={la}>{la}</li>)}
                            </ul>
                        </div>
                    )}

                    {result.key_features && result.key_features.length > 0 && (
                        <div className="bg-brand-card border border-brand-border rounded-3xl p-5">
                            <h3 className="font-semibold text-brand-ink mb-2">Key features</h3>
                            <ul className="text-sm text-brand-ink list-disc list-inside space-y-0.5">
                                {result.key_features.map((k) => <li key={k}>{k}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        {result.habitat && (
                            <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                                <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-1">Habitat</p>
                                <p className="text-sm text-brand-ink">{result.habitat}</p>
                            </div>
                        )}
                        {result.season && (
                            <div className="bg-brand-card border border-brand-border rounded-2xl p-4">
                                <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-1">Season</p>
                                <p className="text-sm text-brand-ink">{result.season}</p>
                            </div>
                        )}
                    </div>

                    {result.preparation && level === 'safe' && (
                        <div className="bg-brand-card border border-brand-border rounded-3xl p-5">
                            <h3 className="font-semibold text-brand-ink mb-2">Preparation</h3>
                            <p className="text-sm text-brand-ink">{result.preparation}</p>
                        </div>
                    )}
                </div>
            )}
            <LogFindModal open={logOpen} onClose={() => setLogOpen(false)} defaultKind="mushroom" defaultSpecies={{ name: result?.name || result?.species || '' }} initialPhotos={photos} autoLocate onLogged={() => navigate('/finds-map')} />
        </div>
    );
}
